import React, {useCallback, useEffect, useState} from "react";
import {Button, ButtonGroup, Checkbox, Loader, Message, Modal, Popover, RadioTile, RadioTileGroup, toaster, Whisper} from "rsuite";
import {
  HandsCorrection_HandsCorrectionKind,
  HandsCorrection_HandsCorrectionMode,
  HandsCorrection_HandsDeviationType,
  HandsCorrectionItemDto,
  HandsCorrectionStateDto,
  HandsDeviationItemDto,
  HandsSensorDeviationDto,
} from "../../../../../../api";
import MechanismApi from "../../../../../../services/Mechanism.api";
import "./HandsCorrection.scss";

interface HandsCorrectionProps {
  mechanismId: string;
  // История сбоев датчиков доступна только администратору
  isAdmin?: boolean;
}

interface ModeCard {
  mode: HandsCorrection_HandsCorrectionMode;
  title: string;
  description: string;
}

const MODE_CARDS: ModeCard[] = [
  {mode: "Off", title: "Выключено", description: "Стрелки не корректируются, данные датчиков только отображаются"},
  {
    mode: "Learning",
    title: "Обучение",
    description: "Автоматически через 24 часа переключится на выбранный режим",
  },
  {mode: "Auto", title: "Автоматическая корректировка", description: "Система сама скорректирует стрелки на часах"},
  {mode: "Notify", title: "Уведомительная корректировка", description: "Только придёт уведомление о сбое"},
];

// Режимы, которые можно включить после обучения
const AFTER_LEARNING_OPTIONS: {mode: HandsCorrection_HandsCorrectionMode; label: string; full: string}[] = [
  {mode: "Notify", label: "Уведомительная", full: "Уведомительная корректировка"},
  {mode: "Auto", label: "Автоматическая", full: "Автоматическая корректировка"},
];

const afterLearningTitle = (mode?: HandsCorrection_HandsCorrectionMode): string =>
  (AFTER_LEARNING_OPTIONS.find(x => x.mode === mode) ?? AFTER_LEARNING_OPTIONS[0]).full;

// Подсказка «i» на плитке режима: как режим работает (по логике сервера)
const MODE_INFO: Record<HandsCorrection_HandsCorrectionMode, {title: string; lines: string[]; hint?: string}> = {
  Off: {
    title: "Режим «Выключено»",
    lines: [
      "Стрелки система не трогает, писем не отправляет.",
      "Датчики при этом читаются: показания шестерён и длительность импульса видны ниже. Если эталона нет, он берётся из событий за последние 7 дней.",
      "Сбои всё равно отслеживаются: если 2 обычных срабатывания подряд отличаются от эталона (больше чем на 1 мин или 0,1 с), появляется красная строка, запись в истории и значок «!» в шапке станции.",
      "Догон после включения станции и подгон стрелок сбоем не считаются.",
    ],
  },
  Learning: {
    title: "Режим «Обучение»",
    lines: [
      "При запуске прежний эталон стирается, и система заново запоминает: длительность импульса ведущего триба, минуту срабатывания минутной, часовой и промежуточной шестерни.",
      "Значение запоминается, когда датчик 3 раза подряд срабатывает в одно и то же время.",
      "Обучение заканчивается через 24 часа или раньше — если часовая шестерня 6 раз подряд сработала в одно время. Условие: уже выучены импульс и минутная (или промежуточная) шестерня.",
      "После обучения система сама включает режим, выбранный на плитке: «Уведомительную корректировку» (по умолчанию) или «Автоматическую». Выбор можно менять и во время обучения — обучение не начнётся заново.",
      "Автоматическая включится, только если выучена длительность импульса ведущего триба; иначе включится уведомительная — об этом будет запись в журнале.",
      "Во время обучения сбои не фиксируются и стрелки не корректируются.",
    ],
    hint: "Перед запуском убедитесь, что стрелки показывают точное время: эталон запоминается с текущего положения.",
  },
  Auto: {
    title: "Режим «Автоматическая корректировка»",
    lines: [
      "Доступен после обучения; может включиться сам, если на плитке «Обучение» выбрана автоматическая. Эталон — минута, в которую минутная шестерня срабатывает при точных стрелках (например, в :59). Каждое новое срабатывание сравнивается с эталоном.",
      "Сбой подтверждён, когда 2 обычных срабатывания подряд (примерно 2 часа) пришли не в эталонную минуту — с допуском 1 мин. Одно случайное отличие сбоем не считается.",
      "Не считаются обычными и пропускаются: догон после включения станции (3 и больше срабатываний подряд чаще, чем раз в 50 мин), первое срабатывание после перерыва больше 70 мин (механизм стоял), подгон кнопкой на механизме и подгон с сайта.",
      "Пример: станцию отключили, потом включили. Механизм догнал время, и датчик снова срабатывает в эталонную минуту — сбоя нет, стрелки не трогаются.",
      "Если после догона датчик стабильно срабатывает в другое время (например, в :11 вместо :59 — механизм не догнал 12 мин), после второго такого срабатывания система читает с механизма положение стрелок и записывает исправленное, на 12 мин меньше. Механизм сам доводит стрелки.",
      "Корректировка пропускается, если механизм в этот момент подводит стрелки. Механизм не ответил — запись «не удалось» и письмо. Часовая шестерня расходится с минутной на 5 мин и больше — письмо «возможно, стрелки заклинило».",
    ],
  },
  Notify: {
    title: "Режим «Уведомительная корректировка»",
    lines: [
      "Стрелки система не трогает — только сообщает.",
      "Сбой подтверждается так же, как в автоматическом режиме: 2 обычных срабатывания подряд не в эталонную минуту. Догон после включения станции, срабатывание после простоя и подгон не учитываются.",
      "При подтверждённом сбое — запись в журнале и письмо «Стрелки отстают/спешат на N мин». Повторное письмо — только когда отклонение изменится.",
      "Если часовая шестерня расходится с минутной на 5 мин и больше — письмо «возможно, стрелки заклинило».",
      "Включается сама после обучения, если на плитке «Обучение» выбрана уведомительная (по умолчанию).",
    ],
  },
};

const KIND_LABELS: Record<HandsCorrection_HandsCorrectionKind, string> = {
  Corrected: "Скорректировано",
  Notified: "Уведомление",
  Failed: "Не удалось",
  JamDetected: "Заклинивание",
  LearningCompleted: "Обучение завершено",
  ModeChanged: "Смена режима",
};

const DEVIATION_TYPE_LABELS: Record<HandsCorrection_HandsDeviationType, string> = {
  SensorDeviation: "Сбой датчика",
  CatchUp: "Догон / подгон",
  ButtonMove: "Подгон кнопкой",
  ManualMove: "Ручной подгон с сайта",
};

// Интервал обновления: события датчиков приходят с блока раз в 7 минут
const REFRESH_MS = 60_000;
const EMPTY = "--";

type GearSensor = "MainGearSensor" | "MinuteGearSensor" | "HourGearSensor" | "IntermediateGearSensor";

interface SensorRow {
  sensor: GearSensor;
  label: string;
  value: string;
  filled: boolean;
}

const SENSOR_NAMES: Record<GearSensor, string> = {
  MainGearSensor: "длительность импульса ведущего триба",
  MinuteGearSensor: "минутная шестерня",
  HourGearSensor: "часовая шестерня",
  IntermediateGearSensor: "промежуточная шестерня",
};

const isTouchDevice = (): boolean =>
  typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

const pad = (value: number): string => String(value).padStart(2, "0");

const formatDate = (value: string | null | undefined): string => {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

const formatTime = (value: string | null | undefined): string => {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatDelta = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return EMPTY;
  return value > 0 ? `+${value}` : value < 0 ? `−${Math.abs(value)}` : "0";
};

const formatPulse = (value: number | null | undefined): string =>
  value === null || value === undefined ? EMPTY : value.toFixed(1).replace(".", ",");

// Минуты 12-часового круга → «3:11»
const formatDialMinutes = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return EMPTY;
  const hours = Math.floor(value / 60) || 12;
  return `${hours}:${pad(value % 60)}`;
};

// Значение датчика в единицах эталона: часовая — минуты 12-часового круга, остальные — минуты
const formatSensorMinute = (sensor: GearSensor, value: number | null | undefined): string => {
  if (value === null || value === undefined) return EMPTY;
  if (sensor === "HourGearSensor") return formatDialMinutes(value);
  if (sensor === "IntermediateGearSensor") return `…${value} мин`;
  return `${value} мин`;
};

const deviationValue = (deviation: HandsSensorDeviationDto): string => {
  const sensor = deviation.sensor as GearSensor;
  if (deviation.currentValue) {
    const delta = deviation.deltaMinutes !== null && deviation.deltaMinutes !== undefined && sensor !== "MainGearSensor"
      ? ` (${formatDelta(deviation.deltaMinutes)} мин)`
      : "";
    return `${deviation.currentValue}${delta}`;
  }
  if (sensor === "MainGearSensor") {
    return `${formatPulse(deviation.currentPulse1)} | ${formatPulse(deviation.currentPulse2)} сек`;
  }
  return `${formatSensorMinute(sensor, deviation.currentMinute)} (${formatDelta(deviation.deltaMinutes)} мин)`;
};

const apiErrorText = (error: unknown): string | null => {
  const body = (error as {body?: unknown} | null)?.body;
  if (typeof body === "string") return body;
  const message = (body as {message?: unknown} | null)?.message;
  return typeof message === "string" ? message : null;
};

const describeLast = (item: HandsCorrectionItemDto | null | undefined): string => {
  if (!item) return "Корректировок не было.";
  const when = `${formatDate(item.createdAt)} в ${formatTime(item.createdAt)}`;
  if (item.kind === "Corrected") return `Последняя корректировка была ${when} на ${formatDelta(item.deltaMinutes)} минут.`;
  return `Последнее событие ${when}: ${item.message ?? KIND_LABELS[item.kind ?? "Corrected"]}.`;
};

// Текущий режим и с какого момента он включён — чтобы старая запись истории
// («…включена уведомительная») не выглядела как текущее состояние
const describeCurrent = (state: HandsCorrectionStateDto | null): string | null => {
  if (!state?.mode) return null;
  const title = MODE_CARDS.find(card => card.mode === state.mode)?.title ?? state.mode;
  const since = state.modeChangedAt ? ` — с ${formatDate(state.modeChangedAt)} в ${formatTime(state.modeChangedAt)}` : "";
  return `Сейчас включено: «${title}»${since}.`;
};

/**
 * Автоматическая корректировка положения стрелок по датчикам шестерён (iBigClock-98)
 */
const HandsCorrection: React.FC<HandsCorrectionProps> = ({mechanismId, isAdmin = false}) => {
  const [state, setState] = useState<HandsCorrectionStateDto | null>(null);
  const [saving, setSaving] = useState<HandsCorrection_HandsCorrectionMode | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<HandsCorrectionItemDto[] | null>(null);
  const [deviationsOpen, setDeviationsOpen] = useState(false);
  const [deviations, setDeviations] = useState<HandsDeviationItemDto[] | null>(null);
  const [showMovements, setShowMovements] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [savingAfter, setSavingAfter] = useState<HandsCorrection_HandsCorrectionMode | null>(null);

  const load = useCallback(async () => {
    if (!mechanismId) return;
    try {
      setState(await MechanismApi.getHandsCorrection(mechanismId));
    } catch (e) {
      console.error("Ошибка при загрузке корректировки стрелок:", e);
    }
  }, [mechanismId]);

  useEffect(() => {
    load().then();
    const timer = window.setInterval(() => load().then(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const mode = state?.mode ?? "Off";
  const canEnable = !!state?.canEnableCorrection;

  const isCardDisabled = (card: HandsCorrection_HandsCorrectionMode): boolean =>
    saving !== null || ((card === "Auto" || card === "Notify") && !canEnable && card !== mode);

  const onSelect = async (card: HandsCorrection_HandsCorrectionMode) => {
    if (isCardDisabled(card)) return;
    if (card === mode && card !== "Learning") return;
    if (card === "Learning" && state?.canEnableCorrection
      && !window.confirm("Начать обучение заново? Выученные данные датчиков будут сброшены.")) return;

    setSaving(card);
    try {
      setState(await MechanismApi.setHandsCorrectionMode(mechanismId, card));
      toaster.push(<Message type="success" closable>Режим корректировки изменён</Message>, {placement: "topEnd"});
    } catch (e) {
      console.error("Ошибка при смене режима корректировки:", e);
      toaster.push(
        <Message type="error" closable>{apiErrorText(e) ?? "Не удалось изменить режим"}</Message>,
        {placement: "topEnd"},
      );
    } finally {
      setSaving(null);
    }
  };

  const afterLearningMode: HandsCorrection_HandsCorrectionMode = state?.afterLearningMode === "Auto" ? "Auto" : "Notify";

  // Выбор режима после обучения: не переключает плитку и не перезапускает обучение
  const onSelectAfterLearning = async (event: React.MouseEvent, next: HandsCorrection_HandsCorrectionMode) => {
    event.preventDefault();
    event.stopPropagation();
    if (next === afterLearningMode || savingAfter !== null) return;

    setSavingAfter(next);
    try {
      setState(await MechanismApi.setHandsAfterLearningMode(mechanismId, next));
      toaster.push(
        <Message type="success" closable>После обучения включится «{afterLearningTitle(next)}»</Message>,
        {placement: "topEnd"},
      );
    } catch (e) {
      console.error("Ошибка при выборе режима после обучения:", e);
      toaster.push(
        <Message type="error" closable>{apiErrorText(e) ?? "Не удалось сохранить режим после обучения"}</Message>,
        {placement: "topEnd"},
      );
    } finally {
      setSavingAfter(null);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistory(null);
    try {
      setHistory(await MechanismApi.getHandsCorrectionHistory(mechanismId));
    } catch (e) {
      console.error("Ошибка при загрузке истории корректировок:", e);
      setHistory([]);
    }
  };

  const dismissDeviations = async () => {
    if (!window.confirm("Сбросить сбои? Красные строки пропадут, в истории сбоев записи сохранятся.")) return;
    setDismissing(true);
    try {
      setState(await MechanismApi.dismissHandsDeviations(mechanismId));
      toaster.push(<Message type="success" closable>Сбои сброшены</Message>, {placement: "topEnd"});
    } catch (e) {
      console.error("Ошибка при сбросе сбоев:", e);
      toaster.push(<Message type="error" closable>{apiErrorText(e) ?? "Не удалось сбросить сбои"}</Message>, {placement: "topEnd"});
    } finally {
      setDismissing(false);
    }
  };

  const openDeviations = async () => {
    setDeviationsOpen(true);
    setDeviations(null);
    try {
      setDeviations(await MechanismApi.getHandsCorrectionDeviations(mechanismId));
    } catch (e) {
      console.error("Ошибка при загрузке истории сбоев:", e);
      setDeviations([]);
    }
  };

  // Период: начало и конец; у ручного подгона с сайта — время сервера
  const deviationPeriod = (item: HandsDeviationItemDto): string => {
    const start = item.since ?? item.createdAt;
    const end = item.type === "ManualMove" ? item.resolvedAt : item.lastSeenAt;
    const from = `${formatDate(start)} ${formatTime(start)}`;
    if (!end) return `${from} — …`;
    const sameDay = formatDate(start) === formatDate(end);
    return `${from} — ${sameDay ? "" : `${formatDate(end)} `}${formatTime(end)}`;
  };

  const deviationValues = (item: HandsDeviationItemDto): string => {
    if (item.type !== "SensorDeviation") {
      if (item.currentValue) return item.currentValue;
      return (item.count ?? 0) > 0 ? `событий: ${item.count}` : EMPTY;
    }
    const delta = item.deltaMinutes !== null && item.deltaMinutes !== undefined ? ` (${formatDelta(item.deltaMinutes)} мин)` : "";
    return `${item.referenceValue ?? EMPTY} → ${item.currentValue ?? EMPTY}${delta}`;
  };

  // Время начала для сортировки (новые сверху)
  const startTime = (item: HandsDeviationItemDto): number => new Date(item.since ?? item.createdAt ?? 0).getTime();
  const sortedDeviations = (deviations ?? [])
    .filter(item => showMovements || item.type === "SensorDeviation")
    .sort((a, b) => startTime(b) - startTime(a));
  const movementsCount = (deviations ?? []).filter(item => item.type !== "SensorDeviation").length;

  const deviationStatus = (item: HandsDeviationItemDto): string => {
    if (!item.resolvedAt) return item.type === "SensorDeviation" ? "Продолжается" : "Идёт";
    if (item.type === "SensorDeviation") return `Прошёл ${formatDate(item.resolvedAt)} ${formatTime(item.resolvedAt)}`;
    return "Завершён";
  };

  const cardDescription = (card: ModeCard): string => {
    if (card.mode === "Learning" && mode === "Learning" && state?.autoSwitchAt) {
      return `Автоматически переключится на «${afterLearningTitle(afterLearningMode)}» ${formatDate(state.autoSwitchAt)} в ${formatTime(state.autoSwitchAt)}`;
    }
    if (card.mode === "Learning") {
      return `Автоматически через 24 часа переключится на «${afterLearningTitle(afterLearningMode)}»`;
    }
    if ((card.mode === "Auto" || card.mode === "Notify") && !canEnable && card.mode !== mode) {
      return `${card.description}. Доступно после обучения`;
    }
    return card.description;
  };

  const pulse = state?.mainGearPulse1 !== null && state?.mainGearPulse1 !== undefined
    ? `${formatPulse(state.mainGearPulse1)} | ${formatPulse(state.mainGearPulse2)} сек`
    : EMPTY;

  const deviationOf = (sensor: GearSensor): HandsSensorDeviationDto | undefined =>
    state?.deviations?.find(x => x.sensor === sensor);

  const modeInfoPopover = (cardMode: HandsCorrection_HandsCorrectionMode) => {
    const info = MODE_INFO[cardMode];
    return (
      <Popover title={info.title} className="hands-correction__popover hands-correction__popover--mode">
        {info.lines.map(line => <p key={line} className="hands-correction__popover-line">{line}</p>)}
        {info.hint && <p className="hands-correction__popover-hint">{info.hint}</p>}
      </Popover>
    );
  };

  // «i» внутри плитки: нажатие открывает подсказку и не переключает режим
  const stopTileSelect = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const deviationPopover = (row: SensorRow, deviation: HandsSensorDeviationDto) => (
    <Popover title={deviation.resolvedAt ? "Был сбой" : "Механизм начал сбоить"} className="hands-correction__popover">
      <p className="hands-correction__popover-line">
        С {formatDate(deviation.since)} в {formatTime(deviation.since)} {SENSOR_NAMES[row.sensor]}:{" "}
        <b>{deviationValue(deviation)}</b> вместо {deviation.referenceValue ?? row.value}.
      </p>
      {deviation.resolvedAt && (
        <p className="hands-correction__popover-line">
          Сбой прошёл {formatDate(deviation.resolvedAt)} в {formatTime(deviation.resolvedAt)} — показания вернулись к эталону.
          Строка пропадёт через 2 дня нормальной работы.
        </p>
      )}
      <p className="hands-correction__popover-line">
        Событий с отличием подряд: {deviation.count}. Последнее — {formatDate(deviation.lastSeenAt)} в {formatTime(deviation.lastSeenAt)}.
      </p>
      <p className="hands-correction__popover-hint">
        Время — по часам блока управления. Эталонные данные не меняются: чтобы принять новые значения, запустите «Обучение».
      </p>
    </Popover>
  );

  const sensors: SensorRow[] = [
    {sensor: "MainGearSensor", label: "Длительность импульса ведущий триб", value: pulse, filled: pulse !== EMPTY},
    {
      sensor: "MinuteGearSensor",
      label: "Минутная шестерня срабатывает в",
      value: state?.minuteGearMinute !== null && state?.minuteGearMinute !== undefined ? `${state.minuteGearMinute} мин` : `${EMPTY} мин`,
      filled: state?.minuteGearMinute !== null && state?.minuteGearMinute !== undefined,
    },
    {
      sensor: "HourGearSensor",
      label: "Часовая шестерня срабатывает в",
      value: state?.hourGearMinute !== null && state?.hourGearMinute !== undefined ? formatDialMinutes(state.hourGearMinute) : `${EMPTY} мин`,
      filled: state?.hourGearMinute !== null && state?.hourGearMinute !== undefined,
    },
    {
      sensor: "IntermediateGearSensor",
      label: "Промежуточная шестерня срабатывает в",
      value: state?.intermediateGearMinute !== null && state?.intermediateGearMinute !== undefined
        ? `…${state.intermediateGearMinute} мин`
        : `${EMPTY} мин`,
      filled: state?.intermediateGearMinute !== null && state?.intermediateGearMinute !== undefined,
    },
  ];

  return (
    <div className="hands-correction">
      <h6 className="hands-correction__title">Автоматическая корректировка положения стрелок</h6>

      <RadioTileGroup
        className="w-100 mb-1"
        aria-label="Режим корректировки положения стрелок"
        value={mode}
        onChange={value => onSelect(value as HandsCorrection_HandsCorrectionMode)}
      >
        <div className="row">
          {MODE_CARDS.map(card => (
            <div key={card.mode} className="col-lg-6 col-md-12 mb-2">
              <RadioTile
                className="h-100 d-flex align-items-start"
                value={card.mode}
                disabled={isCardDisabled(card.mode)}
                label={(
                  <span className="hands-correction__tile-label">
                    {card.title}
                    <Whisper
                      placement="autoVerticalStart"
                      trigger={["hover", "click"]}
                      enterable
                      speaker={modeInfoPopover(card.mode)}
                    >
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={`Как работает режим «${card.title}»`}
                        className="hands-correction__info hands-correction__mode-info"
                        onClick={stopTileSelect}
                      >
                        i
                      </span>
                    </Whisper>
                    {saving === card.mode && <Loader size="xs"/>}
                  </span>
                )}
              >
                <span className="radio-tile-text">{cardDescription(card)}</span>
                {card.mode === "Learning" && (
                  <span className="hands-correction__after-learning">
                    <span className="hands-correction__after-learning-label">После обучения включить:</span>
                    <ButtonGroup size="xs">
                      {AFTER_LEARNING_OPTIONS.map(option => (
                        <Button
                          key={option.mode}
                          appearance={afterLearningMode === option.mode ? "primary" : "ghost"}
                          loading={savingAfter === option.mode}
                          disabled={state === null}
                          aria-pressed={afterLearningMode === option.mode}
                          onClick={event => onSelectAfterLearning(event, option.mode)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </span>
                )}
              </RadioTile>
            </div>
          ))}
        </div>
      </RadioTileGroup>

      {describeCurrent(state) && <p className="hands-correction__current">{describeCurrent(state)}</p>}

      <p className="hands-correction__last">
        {describeLast(state?.lastCorrection)}
        {(state?.correctionsCount ?? 0) > 0 && (
          <Button appearance="link" size="sm" className="hands-correction__history-link" onClick={openHistory}>
            Посмотреть все корректировки
          </Button>
        )}
      </p>

      <div className="hands-correction__title-row">
        <h6 className="hands-correction__title">Данные с датчиков механизма для корректировки</h6>
        {isAdmin && (state?.deviations?.length ?? 0) > 0 && (
          <Button appearance="link" size="sm" className="hands-correction__history-link hands-correction__dismiss"
                  loading={dismissing} onClick={dismissDeviations}>
            Сбросить сбои
          </Button>
        )}
        {isAdmin && (
          <Button appearance="link" size="sm" className="hands-correction__history-link" onClick={openDeviations}>
            История сбоев
          </Button>
        )}
      </div>
      {state?.isMoving && (
        <p className="hands-correction__moving">
          Механизм идёт быстрым ходом (догон после включения часовой станции или подгон) — новые срабатывания сбоем не считаются.
        </p>
      )}
      <div className="hands-correction__sensors">
        {sensors.map(sensor => {
          const deviation = deviationOf(sensor.sensor);
          const classes = [
            "hands-correction__sensor",
            sensor.filled ? "" : "hands-correction__sensor--empty",
            deviation ? "hands-correction__sensor--deviation" : "",
          ].filter(Boolean).join(" ");
          return (
            <div key={sensor.sensor} className={classes}>
              <div className="hands-correction__sensor-main">
                <span className="hands-correction__sensor-label">{sensor.label}</span>
                <span className="hands-correction__sensor-value">{sensor.value}</span>
              </div>
              {deviation && (
                <div className="hands-correction__deviation">
                  <span className="hands-correction__deviation-label">
                    {deviation.resolvedAt ? `Был сбой до ${formatDate(deviation.resolvedAt)}` : "Сейчас"}
                    <Whisper
                      trigger={isTouchDevice() ? "click" : "hover"}
                      enterable
                      placement="autoVertical"
                      speaker={deviationPopover(sensor, deviation)}
                    >
                      <span role="button" aria-label="Когда механизм начал сбоить" className="hands-correction__info">i</span>
                    </Whisper>
                  </span>
                  <span className="hands-correction__sensor-value">{deviationValue(deviation)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Modal open={historyOpen} onClose={() => setHistoryOpen(false)} size="md">
        <Modal.Header>
          <Modal.Title>Корректировки стрелок</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {history === null && <Loader center content="Загрузка..."/>}
          {history !== null && history.length === 0 && <p>Корректировок не было.</p>}
          {history !== null && history.length > 0 && (
            <div className="hands-correction__table-wrap">
              <table className="hands-correction__table">
                <thead>
                <tr>
                  <th>Дата</th>
                  <th>Событие</th>
                  <th className="hands-correction__number">Мин</th>
                  <th>Описание</th>
                </tr>
                </thead>
                <tbody>
                {history.map(item => (
                  <tr key={item.id} className={`hands-correction__row hands-correction__row--${(item.kind ?? "").toLowerCase()}`}>
                    <td>{formatDate(item.createdAt)} {formatTime(item.createdAt)}</td>
                    <td>{item.kind ? KIND_LABELS[item.kind] : EMPTY}</td>
                    <td className="hands-correction__number">{formatDelta(item.deltaMinutes)}</td>
                    <td>{item.message ?? ""}</td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="subtle" onClick={() => setHistoryOpen(false)}>Закрыть</Button>
        </Modal.Footer>
      </Modal>

      <Modal open={deviationsOpen} onClose={() => setDeviationsOpen(false)} size="lg">
        <Modal.Header>
          <Modal.Title>История сбоев датчиков</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="hands-correction__legend">
            Здесь видно, когда датчик начал показывать не то, что зафиксировано в эталоне: например, минутная
            шестерня срабатывает в 41 мин вместо 14. Строка остаётся красной («Продолжается»), пока показания не вернутся к
            эталону. Срабатывания во время догона (после включения часовой станции) и подгона сбоем не считаются.
          </p>
          {deviations !== null && movementsCount > 0 && (
            <Checkbox checked={showMovements} onChange={(_, checked) => setShowMovements(checked)}>
              Показать догон и подгон ({movementsCount})
            </Checkbox>
          )}
          {deviations === null && <Loader center content="Загрузка..."/>}
          {deviations !== null && sortedDeviations.length === 0 && (
            <p>{showMovements ? "Сбоев и подгонов не было." : "Сбоев датчиков не было."}</p>
          )}
          {deviations !== null && sortedDeviations.length > 0 && (
            <div className="hands-correction__table-wrap">
              <table className="hands-correction__table">
                <thead>
                <tr>
                  <th>Период</th>
                  <th>Тип</th>
                  <th>Датчик</th>
                  <th>Было → стало</th>
                  <th>Статус</th>
                </tr>
                </thead>
                <tbody>
                {sortedDeviations.map(item => (
                  <tr key={item.id} className={`hands-correction__row hands-correction__row--${(item.type ?? "").toLowerCase()}`}>
                    <td className="hands-correction__nowrap">{deviationPeriod(item)}</td>
                    <td>{item.type ? DEVIATION_TYPE_LABELS[item.type] : EMPTY}</td>
                    <td>{item.sensor && item.sensor in SENSOR_NAMES ? SENSOR_NAMES[item.sensor as GearSensor] : EMPTY}</td>
                    <td>{deviationValues(item)}</td>
                    <td>{deviationStatus(item)}</td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="hands-correction__popover-hint">
            Время периода — по часам блока управления (у ручного подгона с сайта — по серверу).
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="subtle" onClick={() => setDeviationsOpen(false)}>Закрыть</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HandsCorrection;
